import {
  ContractFileCategory,
  ContractFileInitBodyItem,
  ContractFileResponse,
  ContractNoteFileInitResponse,
} from 'compliance-api/generated';
import { SelectOption } from 'ui';
import { fixNameEnum } from 'ui/utils';

export type SelectedContractFile = {
  file: File;
  file_category?: ContractFileCategory;
};

export const contractFileCategoryOptions: SelectOption<ContractFileCategory | null>[] = [
  { value: null, label: 'Auto' },
  ...Object.values(ContractFileCategory).map(category => ({
    value: category,
    label: fixNameEnum(category),
  })),
];

export const contractFileCategoryLabels: Record<ContractFileCategory, string> = {
  [ContractFileCategory.CoreDocument]: 'Core documents',
  [ContractFileCategory.SupportingDocument]: 'Supporting',
};

export const contractFileToInitItem = ({
  file,
  file_category,
}: SelectedContractFile): ContractFileInitBodyItem => {
  const payload: ContractFileInitBodyItem = {
    file_name: file.name,
    mime_type: file.type || 'application/octet-stream',
    file_size: file.size,
  };

  if (file_category) {
    payload.file_category = file_category;
  }

  return payload;
};

export const uploadContractFiles = async (
  uploads: ContractNoteFileInitResponse[],
  files: File[],
) => {
  const storageItemIds = await Promise.all(
    uploads.map(async (upload, index) => {
      const file = files[index];
      if (!file) return undefined;

      const formData = new FormData();
      Object.entries(upload.presigned_upload_fields || {}).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('file', file);

      const response = await fetch(upload.presigned_upload_url, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Failed to upload ${file.name}`);
      }

      return upload.storage_item_id;
    }),
  );

  return storageItemIds.filter(Boolean) as string[];
};

export const getContractFileName = (file: ContractFileResponse) =>
  file.original_file_name || file.stored_file_name || `File #${file.id}`;

export const getContractFileCategory = (file: ContractFileResponse) =>
  file.file_category || ContractFileCategory.SupportingDocument;

export const formatFileSize = (size: number) => {
  if (size < 1024 * 1024) {
    return `${Math.max(1, Math.round(size / 1024))} KB`;
  }

  return `${Math.round(size / (1024 * 1024))} MB`;
};
